import React, { useEffect, useState, useCallback, useRef } from 'react';
import { 
  initializeItinerarySocket, 
  processItinerarySteps, 
  findCurrentWaypoint,
  sendArriveDestination,
  sendComplete,
  sendDepartHub
} from '../Itinerary/itinerarySocketHelper';
import ItineraryItinerary from '../Itinerary/ItineraryItinerary';
import ItineraryMap from '../Itinerary/ItineraryMap';
import ItineraryCurrentWaypoint from '../Itinerary/ItineraryCurrentWaypoint';
import GeolocationHandler from '../LiveDispatch/GeolocationHandler';
import GpsWarningTag from '../LiveDispatch/GpsWarningTag';
import FlagTripDropoffModal from './FlagTripDropoffModal';
import comms from '../../comms';

/**
 * Calculate distance between two GPS coordinates using Haversine formula
 * @returns {number} Distance in meters
 */
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Check if current location is within threshold distance of hub
 * @param {Object} currentLocation - {lat, lng}
 * @param {number} baseLat - Hub latitude
 * @param {number} baseLon - Hub longitude
 * @param {number} threshold - Distance threshold in meters (default 200m)
 * @returns {boolean}
 */
function isNearHub(currentLocation, baseLat, baseLon, threshold = 200) {
  if (!currentLocation || !baseLat || !baseLon) return false;
  const distance = calculateDistance(currentLocation.lat, currentLocation.lng, baseLat, baseLon);
  return distance <= threshold;
}

/**
 * Group itinerary trips into blocks by hub steps or dropoff time
 * Hub steps take precedence when present; falls back to dropoff-time grouping for backwards compatibility
 */
function getBlocksFromItinerary(itinerary, baseLat, baseLon) {
  if (!itinerary || !Array.isArray(itinerary)) return [];
  
  // Check if there are hub steps in the itinerary
  const hubSteps = itinerary.filter(step => {
    const type = (step.type || step.StepDescription || step.StepName || '').toLowerCase();
    return type === 'hub';
  });
  
  console.log('[getBlocksFromItinerary] Found hub steps:', hubSteps.length, 'from', itinerary.length, 'total steps');
  
  // If hub steps exist, use them to define blocks
  if (hubSteps.length > 0) {
    const blocks = hubSteps.map((hubStep, index) => {
      const hubTime = hubStep.StartDateTime || hubStep.eta;
      // Find all trips (pickup/delivery) that occur before this hub step
      const tripsBeforeHub = itinerary.filter(step => {
        const type = (step.type || step.StepDescription || step.StepName || '').toLowerCase();
        const isTrip = type === 'pickup' || type === 'delivery' || type === 'dropoff';
        if (!isTrip) return false;
        
        const stepTime = step.StartDateTime || step.eta;
        if (!stepTime || !hubTime) return false;
        
        // Trip belongs to this block if its time is before or at the hub time
        // and after the previous hub (if any)
        const prevHub = index > 0 ? hubSteps[index - 1] : null;
        const prevHubTime = prevHub ? (prevHub.StartDateTime || prevHub.eta) : null;
        
        const stepDate = new Date(stepTime);
        const hubDate = new Date(hubTime);
        const prevHubDate = prevHubTime ? new Date(prevHubTime) : new Date(0);
        
        return stepDate <= hubDate && stepDate > prevHubDate;
      });
      
      return {
        blockIndex: index,
        dropoffTime: hubTime,
        hubStep: hubStep,
        trips: tripsBeforeHub
      };
    });
    
    console.log('[getBlocksFromItinerary] Created', blocks.length, 'blocks from hub steps');
    return blocks;
  }
  
  // Fallback: Filter to delivery/dropoff steps only (legacy behavior)
  const deliverySteps = itinerary.filter(step => {
    const type = (step.type || step.StepDescription || step.StepName || '').toLowerCase();
    return type === 'delivery' || type === 'dropoff';
  });
  
  console.log('[getBlocksFromItinerary] No hub steps found, falling back to delivery steps:', deliverySteps.length);
  
  // Group by scheduled dropoff time (hour)
  const blockMap = new Map();
  deliverySteps.forEach(step => {
    const scheduledTime = step.StartDateTime || step.eta;
    if (scheduledTime) {
      const hour = new Date(scheduledTime).getHours();
      if (!blockMap.has(hour)) {
        blockMap.set(hour, { blockIndex: blockMap.size, dropoffTime: scheduledTime, trips: [] });
      }
      blockMap.get(hour).trips.push(step);
    }
  });
  
  return Array.from(blockMap.values()).sort((a, b) => 
    new Date(a.dropoffTime) - new Date(b.dropoffTime)
  );
}

function DemandResponse({
  socket: passedSocket,
  fleetSettings,
  userSettings,
  driverData,
  navigateTo,
  routeDetails: initialRouteDetails,
  selectedVehicle,
  onBack,
  isDispatchMode = false
}) {
  const [googleApiKey, setGoogleApiKey] = useState(null);
  const [map, setMap] = useState(null);
  const [isAccurate, setIsAccurate] = useState(false);
  const [center, setCenter] = useState({ lat: 28.05, lng: -82.4483 });
  const [loading, setLoading] = useState(true);
  const [isPhone, setIsPhone] = useState(false);
  const [vehiclePosition, setVehiclePosition] = useState(null);
  const [showFlagTripModal, setShowFlagTripModal] = useState(false);
  const [tripCreatedToast, setTripCreatedToast] = useState(null);
  const [isCompletingArrivals, setIsCompletingArrivals] = useState(false);
  
  // Route state - managed by socket updates
  const [routeDetails, setRouteDetails] = useState(initialRouteDetails);
  const [currentWaypoint, setCurrentWaypoint] = useState(null);
  const [schedule, setSchedule] = useState([]);
  
  // Demand Response Phase State
  // Phases: 'inbound_pickup' | 'hub_arrival' | 'passenger_boarding' | 'outbound_dropoff'
  const [currentPhase, setCurrentPhase] = useState('inbound_pickup');
  const [outboundPassengerCount, setOutboundPassengerCount] = useState(0);
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0);
  const [lastHubDepartureTime, setLastHubDepartureTime] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [manualHubOverride, setManualHubOverride] = useState(false);
  
  // Temporary pickup mode - when handling a scheduled pickup while having outbound passengers
  const [temporaryPickupMode, setTemporaryPickupMode] = useState(false);
  const [temporaryWaypoint, setTemporaryWaypoint] = useState(null);
  
  // Refs for GPS timing and map reference
  const lastLocationRef = useRef(null);
  const lastServerUpdateRef = useRef(0);
  const mapContainerRef = useRef(null);
  
  // Get hub coordinates from hub step in itinerary (most accurate)
  // Falls back to route end location if no hub step exists
  const getHubCoordinates = useCallback(() => {
    // Find a hub step that has valid coordinates
    // Hub steps use Latitude/Longitude (capital L, full word) from the server
    const hubWithCoords = routeDetails?.itinerary?.find(step => {
      const type = (step.StepDescription || step.StepType || step.type || step.StepName || '').toLowerCase();
      return type === 'hub' && (step.Latitude || step.Lat) && (step.Longitude || step.Lon);
    });
    
    if (hubWithCoords) {
      return {
        lat: hubWithCoords.Latitude || hubWithCoords.Lat,
        lon: hubWithCoords.Longitude || hubWithCoords.Lon
      };
    }
    
    // Fallback to baseLat/baseLon (set by server from first hub step), then startLat/startLon
    return {
      lat: routeDetails?.baseLat || routeDetails?.endLat || routeDetails?.startLat,
      lon: routeDetails?.baseLon || routeDetails?.endLon || routeDetails?.startLon
    };
  }, [routeDetails]);
  
  const hubCoords = getHubCoordinates();
  const baseLat = hubCoords.lat;
  const baseLon = hubCoords.lon;
  
  // Get block transition threshold from fleet settings (default 10 minutes)
  const demandResponseBlockTransitionMins = fleetSettings?.demandResponseBlockTransitionMins ?? 10;
  
  // Track whether we're on a phone vs tablet/desktop
  // Respect fleet setting 'displayOrientation' to force portrait or landscape layout
  useEffect(() => {
    const handleResize = () => {
      // Check if fleet setting 'displayOrientation' is set
      if (fleetSettings?.displayOrientation === 'portrait') {
        setIsPhone(true); // Force portrait layout
      } else if (fleetSettings?.displayOrientation === 'landscape') {
        setIsPhone(false); // Force landscape layout
      } else {
        // Default responsive behavior based on window width
      setIsPhone(window.innerWidth < 768);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fleetSettings?.displayOrientation]);
  
  // Restore demand response state from localStorage on mount
  useEffect(() => {
    const routeId = routeDetails?.routeId;
    if (!routeId) return;
    
    const savedState = localStorage.getItem(`demandResponse_${routeId}`);
    if (savedState) {
      try {
        const { phase, outboundCount, blockIndex, hubDepartureTime } = JSON.parse(savedState);
        if (phase) setCurrentPhase(phase);
        if (typeof outboundCount === 'number') setOutboundPassengerCount(outboundCount);
        if (typeof blockIndex === 'number') setCurrentBlockIndex(blockIndex);
        if (hubDepartureTime) setLastHubDepartureTime(hubDepartureTime);
        console.log('Restored demand response state from localStorage:', { phase, outboundCount, blockIndex, hubDepartureTime });
      } catch (e) {
        console.warn('Failed to restore demand response state:', e);
      }
    }
  }, [routeDetails?.routeId]);
  
  // Persist demand response state to localStorage on state change
  useEffect(() => {
    const routeId = routeDetails?.routeId;
    if (!routeId) return;
    
    const stateToSave = {
      phase: currentPhase,
      outboundCount: outboundPassengerCount,
      blockIndex: currentBlockIndex,
      hubDepartureTime: lastHubDepartureTime
    };
    localStorage.setItem(`demandResponse_${routeId}`, JSON.stringify(stateToSave));
  }, [currentPhase, outboundPassengerCount, currentBlockIndex, lastHubDepartureTime, routeDetails?.routeId]);
  
  // Calculate blocks from itinerary when route details change
  useEffect(() => {
    if (routeDetails?.itinerary) {
      const calculatedBlocks = getBlocksFromItinerary(routeDetails.itinerary, baseLat, baseLon);
      setBlocks(calculatedBlocks);
    }
  }, [routeDetails?.itinerary, baseLat, baseLon]);
  
  // Update lastHubDepartureTime to reflect the most recent completed hub step
  // This ensures flag trips use the correct pickup time from the latest hub visit
  useEffect(() => {
    if (!routeDetails?.itinerary) return;
    
    // Find all completed hub steps
    const completedHubSteps = routeDetails.itinerary.filter(step => {
      const stepType = (step.type || step.StepDescription || step.StepName || '').toLowerCase();
      const isHub = stepType === 'hub';
      const isCompleted = step.isCompleted || 
        (step.StepStatus || '').toLowerCase() === 'complete' || 
        (step.StepStatus || '').toLowerCase() === 'completed';
      return isHub && isCompleted;
    });
    
    if (completedHubSteps.length === 0) return;
    
    // Sort by actual completion time to find the most recent one
    // Priority: actualDateTime (server field) > CompletedDateTime > StartDateTime (scheduled) > eta
    completedHubSteps.sort((a, b) => {
      const timeA = a.actualDateTime || a.CompletedDateTime || a.StartDateTime || a.eta || 0;
      const timeB = b.actualDateTime || b.CompletedDateTime || b.StartDateTime || b.eta || 0;
      return new Date(timeB) - new Date(timeA);
    });
    
    const mostRecentHub = completedHubSteps[0];
    const hubTime = mostRecentHub.actualDateTime || mostRecentHub.CompletedDateTime || mostRecentHub.StartDateTime || mostRecentHub.eta;
    
    if (hubTime && hubTime !== lastHubDepartureTime) {
      console.log('[DemandResponse] Updating lastHubDepartureTime to most recent completed hub:', hubTime);
      setLastHubDepartureTime(hubTime);
    }
  }, [routeDetails?.itinerary, lastHubDepartureTime]);
  
  // Auto-transition to hub_arrival when current waypoint is a hub step
  // This handles both initial hub step display and back-to-back hub steps
  // (e.g., after handleConfirmBoarding(0) returns to inbound_pickup, server completes
  // the hub step, next hub step becomes currentWaypoint, this triggers hub_arrival again)
  useEffect(() => {
    if (!currentWaypoint) return;
    const waypointType = (currentWaypoint.type || currentWaypoint.StepDescription || '').toLowerCase();
    if (waypointType === 'hub' && currentPhase === 'inbound_pickup') {
      console.log('Current waypoint is hub step - auto-transitioning to hub_arrival');
      setCurrentPhase('hub_arrival');
    }
  }, [currentWaypoint, currentPhase]);
  
  // Automatic phase transitions for demand response mode
  useEffect(() => {
    // Skip if not in outbound_dropoff phase or no blocks
    if (currentPhase !== 'outbound_dropoff' || blocks.length === 0) return;
    
    const checkTransition = () => {
      const nextBlock = blocks[currentBlockIndex + 1];
      
      // Transition when vehicle is empty
      if (outboundPassengerCount === 0 && nextBlock) {
        console.log('Vehicle empty - transitioning to next block inbound_pickup');
        setCurrentBlockIndex(currentBlockIndex + 1);
        setCurrentPhase('inbound_pickup');
        setManualHubOverride(false);
        return;
      }
      
      // Transition when next pickup is approaching (within threshold) AND vehicle is empty
      // NOTE: Only transition for approaching pickup when vehicle is empty
      // If passengers are on board, stay in outbound_dropoff phase
      if (outboundPassengerCount === 0 && nextBlock && nextBlock.trips && nextBlock.trips.length > 0) {
        const nextPickupTime = nextBlock.trips[0]?.StartDateTime || nextBlock.trips[0]?.eta;
        if (nextPickupTime) {
          const minutesUntil = (new Date(nextPickupTime) - new Date()) / 60000;
          if (minutesUntil <= demandResponseBlockTransitionMins) {
            console.log('Vehicle empty and next pickup approaching - transitioning to next block inbound_pickup');
            setCurrentBlockIndex(currentBlockIndex + 1);
            setCurrentPhase('inbound_pickup');
            setManualHubOverride(false);
          }
        }
      }
    };
    
    // Check immediately and then every 30 seconds
    checkTransition();
    const intervalId = setInterval(checkTransition, 30000);
    
    return () => clearInterval(intervalId);
  }, [currentPhase, outboundPassengerCount, blocks, currentBlockIndex, demandResponseBlockTransitionMins]);
  
  // Clear demand response state when route changes
  useEffect(() => {
    // When routeId changes, reset phase state for a fresh start
    const prevRouteId = localStorage.getItem('demandResponse_lastRouteId');
    const currentRouteId = routeDetails?.routeId;
    
    if (prevRouteId && currentRouteId && prevRouteId !== currentRouteId) {
      console.log('Route changed - clearing demand response state');
      setCurrentPhase('inbound_pickup');
      setOutboundPassengerCount(0);
      setCurrentBlockIndex(0);
      setLastHubDepartureTime(null);
      setManualHubOverride(false);
      localStorage.removeItem(`demandResponse_${prevRouteId}`);
    }
    
    if (currentRouteId) {
      localStorage.setItem('demandResponse_lastRouteId', currentRouteId);
    }
  }, [routeDetails?.routeId]);
  
  // Load Google Maps API key from fleetSettings
  useEffect(() => {
    if (fleetSettings && fleetSettings.googleApiKey) {
      setGoogleApiKey(fleetSettings.googleApiKey);
      setLoading(false);
    }
  }, [fleetSettings]);
  
  // Initialize socket listeners for itinerary updates
  useEffect(() => {
    if (!passedSocket) {
      console.warn('Socket not provided to DemandResponse - real-time updates disabled');
      return;
    }
    
    console.log('Initializing demand response socket listeners...');
    
    const cleanup = initializeItinerarySocket(passedSocket, (updatedRouteData) => {
      console.log('DemandResponse received route update:', updatedRouteData);
      setRouteDetails(updatedRouteData);
    });
    
    // Listen for demand response trip creation confirmation (inbound trips)
    const handleTripCreatedConfirmation = (response) => {
      console.log('Demand response trip created confirmation:', response);
      if (response.success) {
        setTripCreatedToast({
          message: `Trip #${response.tripNumber} created for ${response.passengerCount} passenger(s)`,
          type: 'success'
        });
        // Auto-hide toast after 4 seconds
        setTimeout(() => setTripCreatedToast(null), 4000);
      }
    };
    
    // Listen for outbound flag trip creation confirmation
    const handleFlagTripCreated = (response) => {
      console.log('Outbound flag trip created confirmation:', response);
      if (response.success) {
        setTripCreatedToast({
          message: `Flag Trip #${response.tripNumber} created for ${response.passengerCount} passenger(s)`,
          type: 'success'
        });
        // Auto-hide toast after 4 seconds
        setTimeout(() => setTripCreatedToast(null), 4000);
      }
    };
    
    passedSocket.on('demandResponseTripCreated', handleTripCreatedConfirmation);
    passedSocket.on('flagTripCreated', handleFlagTripCreated);
    
    return () => {
      cleanup();
      passedSocket.off('demandResponseTripCreated', handleTripCreatedConfirmation);
      passedSocket.off('flagTripCreated', handleFlagTripCreated);
    };
  }, [passedSocket]);
  
  // Process itinerary and find current waypoint when routeDetails changes
  useEffect(() => {
    if (!routeDetails || !routeDetails.itinerary || !routeDetails.trips) {
      setSchedule([]);
      setCurrentWaypoint(null);
      return;
    }
    
    // Process itinerary steps (smart filtering - keeps completed steps appearing after incomplete ones)
    // Pass demandResponseMode: true to include 'hub' steps in the processed output
    const processed = processItinerarySteps(routeDetails.itinerary, routeDetails.trips, { demandResponseMode: true });
    
    // Add vehicleId and routeId to each step
    const enriched = processed.map(step => ({
      ...step,
      vehicleId: selectedVehicle?.Id || selectedVehicle?.id,
      routeId: routeDetails.routeId
    }));
    
    setSchedule(enriched);
    
    // Find current waypoint with start-step priority.
    // CRITICAL: If the start step is incomplete, it MUST be the current waypoint
    // regardless of array order. Some routes have hub ArrivalDateTime earlier than
    // the start step (e.g. route D0E00069: start=08:14, hub=06:00), so a naive
    // .find(!isCompleted) would show the hub before "Begin Work".
    const incompleteStartStep = enriched.find(step => {
      const stepType = (step.type || step.StepDescription || step.StepName || '').toLowerCase();
      return (stepType === 'start' || step.beginStep) && !step.isCompleted;
    });
    const current = incompleteStartStep || findCurrentWaypoint(enriched);
    setCurrentWaypoint(current);
    
    // If no incomplete steps remain, navigate to CompleteShift
    if (!current && enriched.length === 0) {
      console.log('All itinerary steps completed - navigating to CompleteShift');
      if (typeof navigateTo === 'function') {
        navigateTo('completeShift');
      }
    }
    
    // Check if temporary waypoint is no longer in schedule (completed) - auto-clear temporary mode
    if (temporaryPickupMode && temporaryWaypoint) {
      const temporaryTripId = temporaryWaypoint.TripId || temporaryWaypoint.tripId;
      const stillInSchedule = enriched.some(step => {
        const stepTripId = step.TripId || step.tripId;
        const stepType = (step.type || step.StepDescription || '').toLowerCase();
        return stepTripId === temporaryTripId && stepType === 'pickup' && !step.isCompleted;
      });
      
      if (!stillInSchedule) {
        console.log('Temporary waypoint no longer in schedule (completed) - auto-clearing temporary mode');
        setTemporaryPickupMode(false);
        setTemporaryWaypoint(null);
      }
    }
  }, [routeDetails, selectedVehicle, navigateTo, temporaryPickupMode, temporaryWaypoint]);
  
  // Send GPS update to server (throttled to every 30 seconds)
  const sendGPSUpdateToServer = useCallback((location) => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastServerUpdateRef.current;
    
    // Only send to server if 30 seconds have passed since last update
    if (timeSinceLastUpdate >= 30000) {
      if (selectedVehicle && routeDetails && routeDetails.routeId) {
        console.log('DemandResponse - Sending GPS_UPDATE to server:', {
          lat: location.lat,
          lng: location.lng,
          vehicleId: selectedVehicle.Id || selectedVehicle.id,
          routeId: routeDetails.routeId
        });
        
        comms.sendGPSUpdate(
          location.lat,
          location.lng,
          0, // speed
          selectedVehicle.Id || selectedVehicle.id,
          routeDetails.routeId,
          'route',
          null,
          selectedVehicle
        );
        
        lastServerUpdateRef.current = now;
      }
    }
  }, [selectedVehicle, routeDetails]);
  
  const handleLocationUpdate = (newLocation) => {
    // Store the latest location
    lastLocationRef.current = newLocation;
    
    // Immediately update components for real-time functionality
    setCenter(newLocation);
    setVehiclePosition(newLocation);
    if (map) {
      map.panTo(newLocation);
    }
    
    // Send GPS update to server (throttled)
    sendGPSUpdateToServer(newLocation);
    
    // Auto-detect hub arrival for demand response phase transition
    if ((currentPhase === 'inbound_pickup' || currentPhase === 'outbound_dropoff') && baseLat && baseLon) {
      if (isNearHub(newLocation, baseLat, baseLon)) {
        console.log('Auto-detected arrival at hub - transitioning to hub_arrival phase');
        // Reset outbound passenger count when returning to hub (in case of miscounts)
        if (currentPhase === 'outbound_dropoff') {
          console.log('Resetting outbound passenger count to 0 (returned to hub)');
          setOutboundPassengerCount(0);
        }
        // Clear temporary pickup mode if active
        if (temporaryPickupMode) {
          setTemporaryPickupMode(false);
          setTemporaryWaypoint(null);
        }
        setCurrentPhase('hub_arrival');
      }
    }
  };
  
  const onLoad = useCallback((loadedMap) => {
    setMap(loadedMap);
  }, []);

  // Handle trip creation (inbound trip recording) - now opens flag trip modal
  const handleCreateTrip = useCallback(() => {
    setShowFlagTripModal(true);
  }, []);

  // Helper function to check if shift end time has passed
  const isAfterShiftEnd = useCallback(() => {
    // Check if there's an end step in the itinerary with a scheduled time
    const endStep = routeDetails?.itinerary?.find(step => {
      const stepType = (step.type || step.StepDescription || step.StepType || '').toLowerCase();
      return stepType === 'end' || step.endStep === true;
    });
    
    if (!endStep) return false;
    
    // Check for end time in various possible fields
    const endTime = endStep.StartDateTime || endStep.eta || endStep.scheduledTime || routeDetails?.endTime;
    
    if (!endTime) return false;
    
    const endDateTime = new Date(endTime);
    const now = new Date();
    
    return now >= endDateTime;
  }, [routeDetails]);

  // Complete all loaded trips (pickup done, delivery pending) at the hub
  const completeAllLoadedTrips = useCallback(async () => {
    console.log('Completing all loaded trips at hub...');
    
    // Use raw routeDetails data, not the filtered schedule
    // The schedule has completed steps filtered out, but we need to check ALL steps
    const rawItinerary = routeDetails?.itinerary || [];
    const trips = routeDetails?.trips || [];
    
    console.log('Raw itinerary steps:', rawItinerary.map(s => ({
      type: s.StepDescription,
      tripId: s.TripId,
      isCompleted: s.isCompleted,
      StepStatus: s.StepStatus
    })));
    console.log('Trips:', trips.map(t => ({
      id: t.Id || t.id || t.reservation?.id,
      Status: t.Status
    })));
    
    setIsCompletingArrivals(true);
    
    // Find all LOADED trips using multiple detection methods
    const loadedTripIds = new Set();
    
    // Method 1: Check trips array for Status === 'Loaded'
    trips.forEach(trip => {
      const tripId = trip.Id || trip.id || trip.reservation?.id;
      const tripStatus = (trip.Status || '').toLowerCase();
      if (tripStatus === 'loaded' || tripStatus.includes('loaded')) {
        loadedTripIds.add(tripId);
        console.log(`Trip ${tripId} is LOADED (from trips array, Status = ${trip.Status})`);
      }
    });
    
    // Method 2: Check raw itinerary for completed pickup steps
    rawItinerary.forEach(step => {
      const tripId = step.TripId || step.tripId;
      if (!tripId) return;
      
      const stepType = (step.StepDescription || step.type || '').toLowerCase();
      const isPickup = stepType === 'pickup';
      const isStepCompleted = step.isCompleted || 
        (step.StepStatus || '').toLowerCase() === 'complete' || 
        (step.StepStatus || '').toLowerCase() === 'completed';
      
      if (isPickup && isStepCompleted) {
        loadedTripIds.add(tripId);
        console.log(`Trip ${tripId} is LOADED (pickup step completed in raw itinerary)`);
      }
    });
    
    console.log('Loaded trip IDs:', Array.from(loadedTripIds));
    
    // Now find delivery steps from raw itinerary for loaded trips that haven't been completed
    const loadedDeliverySteps = rawItinerary.filter(step => {
      const stepType = (step.StepDescription || step.type || '').toLowerCase();
      const isDelivery = stepType === 'delivery' || stepType === 'dropoff';
      const tripId = step.TripId || step.tripId;
      const isDeliveryCompleted = step.isCompleted || 
        (step.StepStatus || '').toLowerCase() === 'complete' || 
        (step.StepStatus || '').toLowerCase() === 'completed';
      
      // Only include if: it's a delivery, the trip is loaded, and delivery is not yet completed
      const shouldInclude = isDelivery && tripId && loadedTripIds.has(tripId) && !isDeliveryCompleted;
      
      if (isDelivery) {
        console.log(`Checking delivery step for trip ${tripId}: loaded=${loadedTripIds.has(tripId)}, deliveryCompleted=${isDeliveryCompleted}, include=${shouldInclude}`);
      }
      
      return shouldInclude;
    });
    
    console.log(`Found ${loadedDeliverySteps.length} loaded delivery steps to complete at hub`);
    
    if (loadedDeliverySteps.length === 0) {
      // Check if there are any remaining pickup steps (incomplete pickups in the itinerary)
      const hasRemainingPickups = rawItinerary.some(step => {
        const stepType = (step.StepDescription || step.type || '').toLowerCase();
        const isPickup = stepType === 'pickup';
        const isStepCompleted = step.isCompleted || 
          (step.StepStatus || '').toLowerCase() === 'complete' || 
          (step.StepStatus || '').toLowerCase() === 'completed';
        return isPickup && !isStepCompleted;
      });
      
      // Only skip passenger_boarding if:
      // 1. Past shift end time AND
      // 2. No remaining pickups to perform
      if (isAfterShiftEnd() && !hasRemainingPickups) {
        console.log('No loaded trips to complete, past shift end time, and no remaining pickups - not transitioning to passenger_boarding');
        setIsCompletingArrivals(false);
        // Don't change phase - let the end step display logic handle it
        return;
      }
      
      console.log('No loaded trips to complete, transitioning to passenger_boarding');
      setIsCompletingArrivals(false);
      setCurrentPhase('passenger_boarding');
      return;
    }
    
    // Complete each loaded delivery step sequentially
    for (const step of loadedDeliverySteps) {
      const tripId = step.TripId || step.tripId;
      const vehicleId = selectedVehicle?.Id || selectedVehicle?.id;
      const routeId = routeDetails?.routeId;
      
      if (!tripId || !vehicleId || !routeId) {
        console.warn('Missing required data for completing trip:', { tripId, vehicleId, routeId });
        continue;
      }
      
      console.log(`Completing delivery for loaded trip ${tripId}...`);
      
      try {
        // First, mark arrival at destination (hub)
        sendArriveDestination({ tripId, vehicleId, routeId });
        
        // Wait a short moment for the server to process
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Then complete the trip
        sendComplete({ tripId, vehicleId, routeId });
        
        // Wait for route update before processing next trip
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        console.log(`Loaded trip ${tripId} completed at hub`);
      } catch (err) {
        console.error(`Error completing loaded trip ${tripId}:`, err);
      }
    }
    
    // Check if there are any remaining pickup steps (incomplete pickups in the itinerary)
    const hasRemainingPickups = rawItinerary.some(step => {
      const stepType = (step.StepDescription || step.type || '').toLowerCase();
      const isPickup = stepType === 'pickup';
      const isStepCompleted = step.isCompleted || 
        (step.StepStatus || '').toLowerCase() === 'complete' || 
        (step.StepStatus || '').toLowerCase() === 'completed';
      return isPickup && !isStepCompleted;
    });
    
    // Only skip passenger_boarding if:
    // 1. Past shift end time AND
    // 2. No remaining pickups to perform
    if (isAfterShiftEnd() && !hasRemainingPickups) {
      console.log('All loaded trips completed, past shift end time, and no remaining pickups - not transitioning to passenger_boarding');
      setIsCompletingArrivals(false);
      // Don't change phase - let the end step display logic handle it
      return;
    }
    
    console.log('All loaded trips completed, transitioning to passenger_boarding');
    setIsCompletingArrivals(false);
    setCurrentPhase('passenger_boarding');
  }, [selectedVehicle, routeDetails, isAfterShiftEnd]);
  
  // Handle "I'm at Hub" manual override
  const handleManualHubArrival = useCallback(() => {
    console.log('Manual hub arrival triggered');
    setManualHubOverride(true);
    // Reset outbound passenger count when returning to hub (in case of miscounts)
    if (currentPhase === 'outbound_dropoff') {
      console.log('Resetting outbound passenger count to 0 (returned to hub)');
      setOutboundPassengerCount(0);
    }
    // Clear temporary pickup mode if active
    if (temporaryPickupMode) {
      setTemporaryPickupMode(false);
      setTemporaryWaypoint(null);
    }
    setCurrentPhase('hub_arrival');
    // Immediately start completing all loaded trips (fire-and-forget async)
    completeAllLoadedTrips();
  }, [currentPhase, temporaryPickupMode, completeAllLoadedTrips]);
  
  // Handle "Start Outbound" for empty blocks
  const handleStartOutbound = useCallback(() => {
    console.log('Starting outbound phase (empty block)');
    setCurrentPhase('passenger_boarding');
  }, []);
  
  // Handle confirming passengers boarding at hub
  const handleConfirmBoarding = useCallback((passengerCount) => {
    console.log('Confirming boarding:', passengerCount, 'passengers');
    setOutboundPassengerCount(passengerCount);
    
    // Find the current hub step to get its scheduled time
    const currentHubStep = routeDetails?.itinerary?.find(step => {
      const stepType = (step.type || step.StepDescription || step.StepName || '').toLowerCase();
      const isCompleted = step.isCompleted || 
        (step.StepStatus || '').toLowerCase() === 'complete' || 
        (step.StepStatus || '').toLowerCase() === 'completed';
      return stepType === 'hub' && !isCompleted;
    });
    
    // Use current actual time as the departure time (driver is confirming boarding NOW)
    // Previously used scheduled time (StartDateTime/eta) which was incorrect
    const hubDepartureTime = new Date().toISOString();
    console.log('[handleConfirmBoarding] Setting lastHubDepartureTime to:', hubDepartureTime);
    setLastHubDepartureTime(hubDepartureTime);
    
    // Send departHub action to server to record passenger count in hub step's Load field
    // This is sent regardless of passenger count to mark the hub step as complete
    const vehicleId = selectedVehicle?.Id || selectedVehicle?.id;
    if (routeDetails?.routeId && vehicleId) {
      sendDepartHub(
        {
          routeId: routeDetails.routeId,
          vehicleId: vehicleId,
          stepId: currentHubStep?.StepId || currentHubStep?.stepId || currentHubStep?.Id || currentHubStep?.id
        },
        passengerCount,
        vehiclePosition
      );
    }
    
    // Only go to outbound_dropoff phase if passengers are boarding
    // If 0 passengers, skip outbound phase and return to inbound_pickup
    if (passengerCount > 0) {
      setCurrentPhase('outbound_dropoff');
    } else {
      console.log('No passengers boarding - skipping outbound phase, returning to inbound_pickup');
      setCurrentPhase('inbound_pickup');
    }
  }, [selectedVehicle, routeDetails, vehiclePosition]);
  
  // Handle opening flag trip modal for passenger dropoff
  const handleOpenFlagTripModal = useCallback(() => {
    setShowFlagTripModal(true);
  }, []);
  
  // Handle successful flag trip creation (passenger exited)
  const handleFlagTripSuccess = useCallback((droppedCount) => {
    const newCount = Math.max(0, outboundPassengerCount - droppedCount);
    setOutboundPassengerCount(newCount);
    setShowFlagTripModal(false);
    
    // If vehicle is empty, check if we should transition to next block
    if (newCount === 0) {
      const nextBlock = blocks[currentBlockIndex + 1];
      if (nextBlock) {
        // Check if next pickup is approaching
        const nextPickupTime = nextBlock.trips[0]?.StartDateTime;
        if (nextPickupTime) {
          const minutesUntil = (new Date(nextPickupTime) - new Date()) / 60000;
          if (minutesUntil <= demandResponseBlockTransitionMins) {
            console.log('Vehicle empty and next pickup approaching - transitioning to next block');
            setCurrentBlockIndex(currentBlockIndex + 1);
            setCurrentPhase('inbound_pickup');
            setManualHubOverride(false);
          }
        }
      }
    }
  }, [outboundPassengerCount, blocks, currentBlockIndex, demandResponseBlockTransitionMins]);
  
  // Handle loading first itinerary step as temporary waypoint while in outbound mode
  const handleLoadFirstStepAsWaypoint = useCallback((step) => {
    console.log('Loading first step as temporary waypoint while in outbound mode:', step);
    setTemporaryPickupMode(true);
    setTemporaryWaypoint(step);
  }, []);
  
  // Handle completion of temporary pickup (load or noshow) - return to outbound mode
  const handleTemporaryPickupComplete = useCallback(() => {
    console.log('Temporary pickup completed, returning to outbound mode');
    setTemporaryPickupMode(false);
    setTemporaryWaypoint(null);
    // Stay in outbound_dropoff phase since we still have passengers
  }, []);

  // Handle clicking end step in itinerary - make it the current waypoint
  const handleEndStepClick = useCallback((endStep) => {
    console.log('End step clicked in itinerary, setting as current waypoint:', endStep);
    setCurrentWaypoint(endStep);
  }, []);
  
  // Get current block's trips
  const currentBlockTrips = blocks[currentBlockIndex]?.trips || [];

  if (!routeDetails) {
    return (
      <div className="flex h-full min-h-screen items-center justify-center bg-slate-100 px-4">
        <div className="max-w-md rounded-lg bg-white p-6 text-center shadow">
          <h2 className="text-lg font-semibold text-slate-900">No Route Assigned</h2>
          <p className="mt-2 text-sm text-slate-600">
            We couldn&apos;t find any itinerary data for the current driver.
          </p>
          {typeof onBack === 'function' && (
            <button
              onClick={onBack}
              className="mt-4 inline-flex items-center rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              Back
            </button>
          )}
        </div>
      </div>
    );
  }
  
  if (loading) {
    return <div>Loading...</div>;
  }

  // Layout styles for portrait/landscape
  const mapSectionStyle = isPhone
    ? { minHeight: '33vh', padding: '5px', display: 'flex', justifyContent: 'center', alignItems: 'center' }
    : { padding: '5px', flex: '1', display: 'flex', justifyContent: 'center', alignItems: 'center' };

  const currentWaypointStyle = isPhone
    ? { flexShrink: 0 }
    : { flexShrink: 0 };

  const itinerarySectionStyle = isPhone
    ? { flex: '1', overflowY: 'auto' }
    : { flex: '1', overflowY: 'auto' };

  return (
    <div className="bg-slate-100" style={{ height: `var(--app-content-height)`, position: 'relative' }}>
      <GeolocationHandler
        onLocationUpdate={handleLocationUpdate}
        setIsAccurate={setIsAccurate}
        vehicleId={selectedVehicle?.Id || selectedVehicle?.id}
        fleetSettings={fleetSettings}
      />

      {googleApiKey && center && (
        <div className={isPhone ? 'flex flex-col h-full' : 'flex flex-row h-full'}>
          {!isPhone ? (
            /* Desktop/Landscape layout: map on left, currentwaypoint + itinerary on right */
            <>
              {/* Map section - full left side */}
              <div ref={mapContainerRef} style={mapSectionStyle}>
                <ItineraryMap
                  googleApiKey={googleApiKey}
                  isPhone={isPhone}
                  center={center}
                  vehiclePosition={vehiclePosition}
                  currentWaypoint={currentWaypoint}
                  schedule={schedule}
                  isAccurate={isAccurate}
                  onMapLoad={onLoad}
                />
              </div>

              {/* Right side - CurrentWaypoint + Itinerary */}
              <div className="flex flex-col" style={{ flex: '1', overflowY: 'auto' }}>
                {(currentWaypoint || temporaryWaypoint) && schedule.length > 0 && (
                  <div style={currentWaypointStyle}>
                    <ItineraryCurrentWaypoint
                      waypoint={temporaryPickupMode && temporaryWaypoint ? temporaryWaypoint : currentWaypoint}
                      expectedTime={null}
                      isAccurate={isAccurate}
                      isPhone={isPhone}
                      currentLocation={vehiclePosition}
                      onShiftComplete={() => navigateTo('completeShift')}
                      // Demand Response phase props
                      demandResponseMode={true}
                      currentPhase={temporaryPickupMode ? 'temporary_pickup' : currentPhase}
                      setCurrentPhase={setCurrentPhase}
                      outboundPassengerCount={outboundPassengerCount}
                      setOutboundPassengerCount={setOutboundPassengerCount}
                      baseLat={baseLat}
                      baseLon={baseLon}
                      lastHubDepartureTime={lastHubDepartureTime}
                      setLastHubDepartureTime={setLastHubDepartureTime}
                      currentBlockTrips={currentBlockTrips}
                      onManualHubArrival={handleManualHubArrival}
                      onCompleteAllLoadedTrips={completeAllLoadedTrips}
                      isCompletingArrivals={isCompletingArrivals}
                      onStartOutbound={handleStartOutbound}
                      onConfirmBoarding={handleConfirmBoarding}
                      onOpenFlagTripModal={handleOpenFlagTripModal}
                      // Temporary pickup mode props
                      temporaryPickupMode={temporaryPickupMode}
                      onTemporaryPickupComplete={handleTemporaryPickupComplete}
                    />
                  </div>
                )}
                {schedule.length > 1 && (
                  <div style={itinerarySectionStyle}>
                    <ItineraryItinerary
                      isPhone={isPhone}
                      schedule={schedule.slice(1)}
                      currentWaypoint={currentWaypoint}
                      routeDetails={routeDetails}
                      // Demand Response block props
                      blocks={blocks}
                      currentBlockIndex={currentBlockIndex}
                      demandResponseBlockTransitionMins={demandResponseBlockTransitionMins}
                      // Outbound mode - allow clicking first step to load as waypoint
                      outboundModeActive={currentPhase === 'outbound_dropoff' && outboundPassengerCount > 0 && !temporaryPickupMode}
                      onFirstStepClick={handleLoadFirstStepAsWaypoint}
                      // Demand response mode - consolidate dropoffs
                      demandResponseMode={true}
                      onHubDropoffClick={handleManualHubArrival}
                      // End step click handler
                      onEndStepClick={handleEndStepClick}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Mobile/Portrait layout: map at top (33vh), currentwaypoint (350px), then itinerary (rest) */
            <>
              {/* Map section - 33vh minimum */}
              <div ref={mapContainerRef} style={mapSectionStyle}>
                <ItineraryMap
                  googleApiKey={googleApiKey}
                  isPhone={isPhone}
                  center={center}
                  vehiclePosition={vehiclePosition}
                  currentWaypoint={currentWaypoint}
                  schedule={schedule}
                  isAccurate={isAccurate}
                  onMapLoad={onLoad}
                />
              </div>

              {/* CurrentWaypoint section - fixed 350px */}
              {(currentWaypoint || temporaryWaypoint) && schedule.length > 0 && (
                <div style={currentWaypointStyle}>
                  <ItineraryCurrentWaypoint
                    waypoint={temporaryPickupMode && temporaryWaypoint ? temporaryWaypoint : currentWaypoint}
                    expectedTime={null}
                    isAccurate={isAccurate}
                    isPhone={isPhone}
                    currentLocation={vehiclePosition}
                    onShiftComplete={() => navigateTo('completeShift')}
                    // Demand Response phase props
                    demandResponseMode={true}
                    currentPhase={temporaryPickupMode ? 'temporary_pickup' : currentPhase}
                    setCurrentPhase={setCurrentPhase}
                    outboundPassengerCount={outboundPassengerCount}
                    setOutboundPassengerCount={setOutboundPassengerCount}
                    baseLat={baseLat}
                    baseLon={baseLon}
                    lastHubDepartureTime={lastHubDepartureTime}
                    setLastHubDepartureTime={setLastHubDepartureTime}
                    currentBlockTrips={currentBlockTrips}
                    onManualHubArrival={handleManualHubArrival}
                    onCompleteAllLoadedTrips={completeAllLoadedTrips}
                    isCompletingArrivals={isCompletingArrivals}
                    onStartOutbound={handleStartOutbound}
                    onConfirmBoarding={handleConfirmBoarding}
                    onOpenFlagTripModal={handleOpenFlagTripModal}
                    // Temporary pickup mode props
                    temporaryPickupMode={temporaryPickupMode}
                    onTemporaryPickupComplete={handleTemporaryPickupComplete}
                  />
                </div>
              )}

              {/* Itinerary section - rest of viewport */}
              {schedule.length > 1 && (
                <div style={itinerarySectionStyle}>
                  <ItineraryItinerary
                    isPhone={isPhone}
                    schedule={schedule.slice(1)}
                    currentWaypoint={currentWaypoint}
                    routeDetails={routeDetails}
                    // Demand Response block props
                    blocks={blocks}
                    currentBlockIndex={currentBlockIndex}
                    demandResponseBlockTransitionMins={demandResponseBlockTransitionMins}
                    // Outbound mode - allow clicking first step to load as waypoint
                    outboundModeActive={currentPhase === 'outbound_dropoff' && outboundPassengerCount > 0 && !temporaryPickupMode}
                    onFirstStepClick={handleLoadFirstStepAsWaypoint}
                    // Demand response mode - consolidate dropoffs
                    demandResponseMode={true}
                    onHubDropoffClick={handleManualHubArrival}
                    // End step click handler
                    onEndStepClick={handleEndStepClick}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Fixed bottom button for recording passenger exits */}
      <button
        onClick={handleCreateTrip}
        className="fixed opacity-70 bottom-4 right-16 z-50 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-full shadow-lg transition-all duration-200 flex items-center gap-2"
        style={{
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        <span className="hidden sm:inline">Record Passengers</span>
      </button>

      {/* Flag Trip Dropoff Modal (for passenger exits) */}
      <FlagTripDropoffModal
        isOpen={showFlagTripModal}
        onClose={() => setShowFlagTripModal(false)}
        maxPassengers={outboundPassengerCount}
        currentLocation={vehiclePosition}
        baseLat={baseLat}
        baseLon={baseLon}
        lastHubDepartureTime={lastHubDepartureTime}
        routeId={routeDetails?.routeId}
        vehicleId={selectedVehicle?.Id || selectedVehicle?.id}
        driverId={userSettings?.Id || userSettings?.id || selectedVehicle?.DriverId || selectedVehicle?.driverId}
        onSuccess={handleFlagTripSuccess}
      />

      {/* Toast notification for trip creation */}
      {tripCreatedToast && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[1200] animate-fade-in">
          <div className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 ${
            tripCreatedToast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          } text-white`}>
            {tripCreatedToast.type === 'success' ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span className="font-medium">{tripCreatedToast.message}</span>
            <button 
              onClick={() => setTripCreatedToast(null)}
              className="ml-2 hover:opacity-80"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {!isAccurate && (
        <GpsWarningTag onLocationUpdate={handleLocationUpdate} setIsAccurate={setIsAccurate} />
      )}
    </div>
  );
}

export default DemandResponse;

